import { parseCommandByte } from './ProtocolHeader';
import {
  RT_MONITOR_COMMAND,
  RT_MONITOR_START,
  RT_MONITOR_STOP,
} from './RtMonitor';

/**
 * 4.0 RT 校准配置（0xF3）。
 *
 * 主机开始包带上左右各 10 字节 DRV2624_Cal_Config_t：
 *   byte3 = 0xF3
 *   byte4 = 1 开始 / 0 结束 / 2 保存
 *   开始：byte5-14 左键配置，byte15-24 右键配置
 *   保存：byte5-8 是 4 字节魔数（高字节在前）
 *     0x00000000 用户自己校准
 *     0x90015633 出厂校准
 *   设备回包：
 *     byte4 = 1    已开始（短确认）
 *     byte4 = 0xFF 结果包，byte5 左键 / byte6 右键
 *       0 失败 / 1 成功 / 2 未找到
 *
 * 每侧 10 字节（前 8 项顺序不变，末尾追加 Blanking / Sample）：
 *   [0] REG_CONTROL1      0x08，固定 0x80（闭环播放）
 *   [1] REG_RATED_VOLTAGE 0x1F，固定 0x68（约 1.85 Vrms）
 *   [2] REG_OD_CLAMP      0x20，固定 0xA9（峰值约 3.59 V）
 *   [3] REG_DRIVE_TIME    0x27[4:0]，由频率算出，上限 31
 *   [4] REG_AUTO_CAL_TIME 0x2A，固定 0x02（1000 ms）
 *   [5] REG_CONTROL3      0x23，固定 0x36（BEMF_GAIN=2）
 *   [6] REG_OL_PERIOD_H   0x2E[1:0]，N 的高 2 位
 *   [7] REG_OL_PERIOD_L   0x2F，N 的低 8 位
 *   [8] REG_BLANKING_IDISS 0x28，固定 0x31（BLANKING=75 µs，IDISS=25 µs）
 *   [9] REG_SAMPLE_ZC     0x29，固定 0x0C（SAMPLE=300 µs）
 *
 * Drive Time：N = (5000000/f − 5000) / 1000，上限 31。
 * Period：N = (1e9/f + 12307) / 24615，范围 0~1023。
 *   reg_2E = (N >> 8) & 0x03
 *   reg_2F = N & 0xFF
 * 170 Hz → Drive 0x18，Period N=239 → H=0x00、L=0xEF。
 * 173 Hz → Drive 0x17，Period N=235 → H=0x00、L=0xEB。
 */
export const RT_CALIBRATE_CONFIG_COMMAND = RT_MONITOR_COMMAND;
export const RT_CALIBRATE_CONFIG_TYPE = 0xf3;
export const RT_CALIBRATE_CONFIG_START = RT_MONITOR_START;
export const RT_CALIBRATE_CONFIG_STOP = RT_MONITOR_STOP;
export const RT_CALIBRATE_CONFIG_SAVE = 2;
export const RT_CALIBRATE_CONFIG_SIDE_BYTES = 10;
export const RT_CALIBRATE_SAVE_USER = 0x00000000;
export const RT_CALIBRATE_SAVE_FACTORY = 0x90015633;
export const RT_CALIBRATE_CONFIG_RESULT = 0xff;
export const RT_CALIBRATE_SIDE_FAIL = 0;
export const RT_CALIBRATE_SIDE_SUCCESS = 1;
export const RT_CALIBRATE_SIDE_NOT_FOUND = 2;
export const RT_CALIBRATE_CONFIG_ACK_TIMEOUT_MS = 2000;
export const RT_CALIBRATE_CONFIG_RESULT_TIMEOUT_MS = 8000;

export const DRV2624_DEFAULT_FREQ_HZ = 170;
export const DRV2624_FREQ_RANGE = Object.freeze({ min: 80, max: 400 });
export const DRV2624_CONTROL1 = 0x80;
export const DRV2624_RATED_VOLTAGE = 0x68;
export const DRV2624_OD_CLAMP = 0xa9;
export const DRV2624_DRIVE_TIME = 0x18;
export const DRV2624_AUTO_CAL_TIME = 0x02;
export const DRV2624_CONTROL3 = 0x36;
export const DRV2624_BLANKING_IDISS = 0x31;
export const DRV2624_SAMPLE_ZC = 0x0c;
export const DRV2624_OL_PERIOD_MAX = 1023;

export const RT_CALIBRATE_CONFIG_OFFSETS = Object.freeze({
  action: 4,
  left: 5,
  right: 15,
  leftResult: 5,
  rightResult: 6,
});

export const RT_CALIBRATE_CONFIG_MIN_LENGTH = 25;

const clampU8 = (value, max) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return 0;
  if (number > max) return max & 0xff;
  return number & 0xff;
};

const clampByte = (value) => clampU8(Math.round(Number(value) || 0), 255);

const clampFrequencyHz = (value) => {
  const number = Math.round(Number(value));
  if (!Number.isFinite(number)) return DRV2624_DEFAULT_FREQ_HZ;
  return Math.max(
    DRV2624_FREQ_RANGE.min,
    Math.min(DRV2624_FREQ_RANGE.max, number)
  );
};

/** 0x27[4:0]：N = (5000000/f − 5000) / 1000，上限 31 */
export function calcDriveTime(frequencyHz) {
  const freq = Math.round(Number(frequencyHz));
  if (!Number.isFinite(freq) || freq <= 0) return 0;
  return clampU8(Math.trunc((Math.trunc(5_000_000 / freq) - 5000) / 1000), 31);
}

/** N = (1000000000/f + 12307) / 24615，范围 0~1023 */
export function calcOlPeriod(frequencyHz) {
  const freq = Math.round(Number(frequencyHz));
  if (!Number.isFinite(freq) || freq <= 0) return 0;
  const period = Math.trunc(
    (Math.trunc(1_000_000_000 / freq) + 12307) / 24615
  );
  return Math.max(0, Math.min(DRV2624_OL_PERIOD_MAX, period));
}

/** 10 位 Period 拆成 0x2E 高 2 位、0x2F 低 8 位 */
export function packOlPeriod(period) {
  const value = Math.max(
    0,
    Math.min(DRV2624_OL_PERIOD_MAX, Math.round(Number(period) || 0))
  );
  return {
    olPeriodH: (value >> 8) & 0x03,
    olPeriodL: value & 0xff,
  };
}

export function emptyCalConfig() {
  return {
    control1: DRV2624_CONTROL1,
    ratedVoltage: DRV2624_RATED_VOLTAGE,
    odClamp: DRV2624_OD_CLAMP,
    driveTime: DRV2624_DRIVE_TIME,
    autoCalTime: DRV2624_AUTO_CAL_TIME,
    control3: DRV2624_CONTROL3,
    olPeriodH: 0,
    olPeriodL: 0,
    blankingIdiss: DRV2624_BLANKING_IDISS,
    sampleZc: DRV2624_SAMPLE_ZC,
  };
}

/**
 * 按频率生成一侧 DRV2624 校准配置。
 * Drive Time、Period 随频率变化，其余固定。
 * @param {{frequencyHz?:number}} options
 */
export function buildDrv2624CalConfig({
  frequencyHz = DRV2624_DEFAULT_FREQ_HZ,
} = {}) {
  const freq = clampFrequencyHz(frequencyHz);
  return {
    ...emptyCalConfig(),
    driveTime: calcDriveTime(freq),
    ...packOlPeriod(calcOlPeriod(freq)),
  };
}

export function packCalConfig(config = {}) {
  const fallback = emptyCalConfig();
  const packedPeriod =
    config.olPeriodH == null && config.olPeriodL == null
      ? packOlPeriod(config.olPeriod)
      : null;
  return [
    clampByte(config.control1 ?? fallback.control1),
    clampByte(config.ratedVoltage ?? fallback.ratedVoltage),
    clampByte(config.odClamp ?? fallback.odClamp),
    clampByte(config.driveTime ?? fallback.driveTime),
    clampByte(config.autoCalTime ?? fallback.autoCalTime),
    clampByte(config.control3 ?? fallback.control3),
    clampU8(config.olPeriodH ?? packedPeriod?.olPeriodH ?? fallback.olPeriodH, 3),
    clampByte(config.olPeriodL ?? packedPeriod?.olPeriodL ?? fallback.olPeriodL),
    clampByte(config.blankingIdiss ?? fallback.blankingIdiss),
    clampByte(config.sampleZc ?? fallback.sampleZc),
  ];
}

export function unpackCalConfig(bytes) {
  const source = Array.isArray(bytes) ? bytes : [];
  return {
    control1: clampByte(source[0] ?? DRV2624_CONTROL1),
    ratedVoltage: clampByte(source[1] ?? DRV2624_RATED_VOLTAGE),
    odClamp: clampByte(source[2] ?? DRV2624_OD_CLAMP),
    driveTime: clampByte(source[3] ?? DRV2624_DRIVE_TIME),
    autoCalTime: clampByte(source[4] ?? DRV2624_AUTO_CAL_TIME),
    control3: clampByte(source[5] ?? DRV2624_CONTROL3),
    olPeriodH: clampU8(source[6], 3),
    olPeriodL: clampByte(source[7]),
    blankingIdiss: clampByte(source[8] ?? DRV2624_BLANKING_IDISS),
    sampleZc: clampByte(source[9] ?? DRV2624_SAMPLE_ZC),
  };
}

const toSideBytes = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return packCalConfig(value);
  }
  const source = Array.isArray(value) ? value : [];
  return Array.from({ length: RT_CALIBRATE_CONFIG_SIDE_BYTES }, (_, index) =>
    clampByte(source[index])
  );
};

const copySideBytes = (data, offset) =>
  Array.from(
    data.slice(offset, offset + RT_CALIBRATE_CONFIG_SIDE_BYTES),
    (value) => Number(value) & 0xff
  );

export const isRtCalibrateConfigAction = (value) =>
  Number(value) === RT_CALIBRATE_CONFIG_STOP ||
  Number(value) === RT_CALIBRATE_CONFIG_START ||
  Number(value) === RT_CALIBRATE_CONFIG_SAVE;

export const isRtCalibrateSaveToken = (value) =>
  Number(value) === RT_CALIBRATE_SAVE_USER ||
  Number(value) === RT_CALIBRATE_SAVE_FACTORY;

/** 保存魔数按高字节在前写入 byte5-8 */
export function packCalibrateSaveToken(token) {
  const value = Number(token) >>> 0;
  return [
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff,
  ];
}

/**
 * 结束包只写 byte4=0。开始包必须带左右配置。保存包 byte4=2，带 4 字节魔数。
 * @param {number} action
 * @param {{left?:object|number[],right?:object|number[],token?:number}} [extra]
 */
export function encodeRtCalibrateConfigControl(action, extra = {}) {
  if (!isRtCalibrateConfigAction(action)) {
    throw new Error('RT 校准配置控制只能是 1（开始）、0（结束）或 2（保存）');
  }
  if (Number(action) === RT_CALIBRATE_CONFIG_STOP) {
    const data = new Uint8Array(64);
    data[0] = RT_CALIBRATE_CONFIG_COMMAND;
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = RT_CALIBRATE_CONFIG_TYPE;
    data[4] = RT_CALIBRATE_CONFIG_STOP;
    return data;
  }
  if (Number(action) === RT_CALIBRATE_CONFIG_SAVE) {
    return encodeRtCalibrateConfigSave(extra.token);
  }
  return encodeRtCalibrateConfigStart(extra.left, extra.right);
}

/**
 * 保存包：byte4=2，byte5-8 是用户/出厂魔数。
 * @param {number} token - 0x00000000 用户 / 0x90015633 出厂
 */
export function encodeRtCalibrateConfigSave(token) {
  if (!isRtCalibrateSaveToken(token)) {
    throw new Error('RT 校准保存只能是用户(0)或出厂(0x90015633)');
  }
  const data = new Uint8Array(64);
  data[0] = RT_CALIBRATE_CONFIG_COMMAND;
  data[1] = 0x00;
  data[2] = 0x80 | 5;
  data[3] = RT_CALIBRATE_CONFIG_TYPE;
  data[4] = RT_CALIBRATE_CONFIG_SAVE;
  data.set(packCalibrateSaveToken(token), 5);
  return data;
}

/**
 * 开始包：byte4=1，byte5-14 左键，byte15-24 右键。
 * @param {object|number[]} [left]
 * @param {object|number[]} [right]
 */
export function encodeRtCalibrateConfigStart(left, right) {
  const data = new Uint8Array(64);
  const leftBytes = toSideBytes(left ?? buildDrv2624CalConfig());
  const rightBytes = toSideBytes(right ?? leftBytes);
  data[0] = RT_CALIBRATE_CONFIG_COMMAND;
  data[1] = 0x00;
  data[2] = 0x80 | (1 + RT_CALIBRATE_CONFIG_SIDE_BYTES * 2);
  data[3] = RT_CALIBRATE_CONFIG_TYPE;
  data[4] = RT_CALIBRATE_CONFIG_START;
  data.set(leftBytes, RT_CALIBRATE_CONFIG_OFFSETS.left);
  data.set(rightBytes, RT_CALIBRATE_CONFIG_OFFSETS.right);
  return data;
}

export function parseRtCalibrateConfig(data) {
  if (!data || data.length < RT_CALIBRATE_CONFIG_MIN_LENGTH) return null;
  if (parseCommandByte(data[0]).command !== RT_CALIBRATE_CONFIG_COMMAND) {
    return null;
  }
  if (data[3] !== RT_CALIBRATE_CONFIG_TYPE) return null;
  if (Number(data[RT_CALIBRATE_CONFIG_OFFSETS.action]) !== RT_CALIBRATE_CONFIG_START) {
    return null;
  }
  if ((Number(data[2]) & 0x7f) === 1) return null;

  const left = copySideBytes(data, RT_CALIBRATE_CONFIG_OFFSETS.left);
  const right = copySideBytes(data, RT_CALIBRATE_CONFIG_OFFSETS.right);
  return {
    left: unpackCalConfig(left),
    right: unpackCalConfig(right),
    leftBytes: left,
    rightBytes: right,
  };
}

export const isRtCalibrateConfigSample = (data) =>
  parseRtCalibrateConfig(data) != null;

export const isRtCalibrateConfigPacket = (data) =>
  !!data &&
  data.length > 3 &&
  parseCommandByte(data[0]).command === RT_CALIBRATE_CONFIG_COMMAND &&
  data[3] === RT_CALIBRATE_CONFIG_TYPE;

export const isRtCalibrateSideResult = (value) => {
  const code = Number(value);
  return (
    code === RT_CALIBRATE_SIDE_FAIL ||
    code === RT_CALIBRATE_SIDE_SUCCESS ||
    code === RT_CALIBRATE_SIDE_NOT_FOUND
  );
};

export const isRtCalibrateConfigAck = (data) =>
  isRtCalibrateConfigPacket(data) &&
  Number(data[RT_CALIBRATE_CONFIG_OFFSETS.action]) === RT_CALIBRATE_CONFIG_START;

export const isRtCalibrateConfigResult = (data) =>
  isRtCalibrateConfigPacket(data) &&
  Number(data[RT_CALIBRATE_CONFIG_OFFSETS.action]) ===
    RT_CALIBRATE_CONFIG_RESULT &&
  isRtCalibrateSideResult(data[RT_CALIBRATE_CONFIG_OFFSETS.leftResult]) &&
  isRtCalibrateSideResult(data[RT_CALIBRATE_CONFIG_OFFSETS.rightResult]);

export function parseRtCalibrateConfigResult(data) {
  if (!isRtCalibrateConfigResult(data)) return null;
  const left = Number(data[RT_CALIBRATE_CONFIG_OFFSETS.leftResult]);
  const right = Number(data[RT_CALIBRATE_CONFIG_OFFSETS.rightResult]);
  return {
    left,
    right,
    success:
      left === RT_CALIBRATE_SIDE_SUCCESS && right === RT_CALIBRATE_SIDE_SUCCESS,
  };
}

export function formatRtCalibrateSideResult(value) {
  const code = Number(value);
  if (code === RT_CALIBRATE_SIDE_SUCCESS) return '成功';
  if (code === RT_CALIBRATE_SIDE_NOT_FOUND) return '未找到';
  return '失败';
}

/** 组一包结果回包：byte4=0xFF，byte5 左 / byte6 右。 */
export function encodeRtCalibrateConfigResult(left, right = left) {
  if (!isRtCalibrateSideResult(left) || !isRtCalibrateSideResult(right)) {
    throw new Error('左右校准结果只能是 0（失败）、1（成功）或 2（未找到）');
  }
  const data = new Uint8Array(64);
  data[0] = RT_CALIBRATE_CONFIG_COMMAND;
  data[1] = 0x00;
  data[2] = 0x80 | 4;
  data[3] = RT_CALIBRATE_CONFIG_TYPE;
  data[4] = RT_CALIBRATE_CONFIG_RESULT;
  data[RT_CALIBRATE_CONFIG_OFFSETS.leftResult] = Number(left);
  data[RT_CALIBRATE_CONFIG_OFFSETS.rightResult] = Number(right);
  return data;
}

export const formatRtCalibrateConfigBytes = (value) =>
  toSideBytes(value)
    .map((item) => item.toString(16).padStart(2, '0').toUpperCase())
    .join(' ');

export function encodeRtCalibrateConfigSample(sample = {}) {
  return encodeRtCalibrateConfigStart(sample.left, sample.right);
}
